import { Badge, Button, Checkbox, Spinner, TextArea } from '@cimplico/components';
import { PencilIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { ReactNode, useState } from 'react';
import { toastNotification } from '../../components/toast/Toast';
import { useFileRecord } from '../../queries/use-file-record';
import { useUpdateFileRecords } from '../../queries/use-update-file-records';
import { FileStatusEnum, FileRecordTypeEnum } from '../../types/file-record';
import { User, UserOverview } from '../../types/user';
import { formatDateTime, formatMixedCase } from '../../utilities/formatting';
import { bytesToMB, isFolderType } from './file-utils';
import FileReferences from './FileReferences';
import FilePathSection from './FilePathSection';
import RecordTypeIcon from './RecordTypeIcon';

type FileRecordDetailsProps = {
    fileId: string;
    readOnly: boolean;
    inContext: boolean;
    onClose: () => void;
    onGoToFolder: (folderId: string, activeFileId?: string) => void;
};

function FileRecordDetails({ fileId, readOnly, inContext, onClose, onGoToFolder }: FileRecordDetailsProps): JSX.Element {
    const [editingDescription, setEditingDescription] = useState(false);
    const [description, setDescription] = useState<string | undefined>();
    const [updateReferences, setUpdateReferences] = useState<boolean>(true);
    const fileRecordQuery = useFileRecord(fileId);
    const updateFileRecordQuery = useUpdateFileRecords();

    if (fileRecordQuery.isLoading) {
        return <></>;
    }

    if (!fileRecordQuery.isSuccess) {
        return <></>;
    }

    const { file } = fileRecordQuery.data;

    function handleEdit(): void {
        updateFileRecordQuery.mutate(
            [
                {
                    id: fileId,
                    fields: { description: description === '' ? null : description },
                    updateReferences: updateReferences,
                },
            ],
            {
                onSuccess: () => {
                    setEditingDescription(false);
                    setUpdateReferences(true);
                    toastNotification('success', 'File description updated!');
                },
                onError: () => {
                    toastNotification('error', 'Unable to update file description!');
                },
            }
        );
    }

    return (
        <div className="relative">
            {/* SPINNER */}
            {fileRecordQuery.isFetching && (
                <div className="absolute bg-white/60 top-0 left-0 h-full w-full">
                    <div className="absolute top-[50px] left-1/2 -translate-x-1/2">
                        <Spinner />
                    </div>
                </div>
            )}
            {/* CONTENT */}
            <div className="h-full px-6">
                <section className="bg-white flex items-start sticky top-0 justify-between pt-2 w-full">
                    <div className="flex">
                        <RecordTypeIcon recordType={file.recordType} system={file.systemRecord} />
                        <p className="font-medium text-lg break-words text-gray-800 w-[220px] pl-4">{file.name}</p>
                    </div>
                    <div className="cursor-pointer -mr-4">
                        <Button type="hollow" size="sm" leftIcon={<XMarkIcon />} onClick={onClose} />
                    </div>
                </section>

                {/* DESCRIPTION */}
                <section className="mt-4">
                    <div className={`flex justify-between items-center text-gray-800 pb-2 ${editingDescription ? '' : 'border-b'}`}>
                        <p className="font-medium">Description</p>
                        {!editingDescription && !fileRecordQuery.data.file.locked && !readOnly && (
                            <PencilIcon className="w-4 h-4 cursor-pointer" onClick={(): void => setEditingDescription(true)} />
                        )}
                    </div>
                    {editingDescription ? (
                        <div className="flex flex-col gap-2">
                            <TextArea
                                placeholder="Add a description..."
                                onChange={setDescription}
                                isDisabled={updateFileRecordQuery.isLoading}
                                defaultValue={fileRecordQuery.data.file.description ?? ''}
                            />
                            <Checkbox
                                label="Apply to all references of this file record."
                                checked={updateReferences}
                                onChange={(): void => setUpdateReferences(!updateReferences)}
                            />
                            <div className="flex justify-end gap-4 pt-2">
                                <Button type="hollow" label="Cancel" size="xs" onClick={(): void => setEditingDescription(false)} />
                                <Button
                                    type="primary"
                                    label={updateFileRecordQuery.isLoading ? 'Saving...' : 'Save'}
                                    size="xs"
                                    onClick={handleEdit}
                                />
                            </div>
                        </div>
                    ) : (
                        <p className="mt-4 text-sm">
                            {file.description ? (
                                <span className="text-gray-800">{file.description}</span>
                            ) : (
                                <span className="text-gray-500">No description.</span>
                            )}
                        </p>
                    )}
                </section>

                <section className="mt-8">
                    <p className="font-medium pb-2 border-b text-gray-800">Information</p>
                    {/* CREATED BY */}
                    <CreatedByStat user={file.createdBy ? file.createdBy : undefined} />

                    {/* CREATED AT */}
                    <FileStat label="Added" value={formatDateTime(file.createdAt)} />

                    {/* UPDATEDT AT */}
                    <FileStat label="Modified" value={formatDateTime(file.updatedAt)} />

                    {/* RECORD TYPE */}
                    <FileStat label="Record Type" value={formatMixedCase(file.recordType)} />

                    {/* SYSTEM GENERATED */}
                    <FileStat label="System Generated" value={file.systemRecord ? 'True' : 'False'} />

                    {/* HIDDEN */}
                    <FileStat label="Hidden" value={file.hidden ? 'True' : 'False'} />

                    {/* ITEM COUNT */}
                    <FileStat label="Total Items" value={file.itemCount !== undefined && file.itemCount !== null ? file.itemCount : '-'} />

                    {/* SIZE */}
                    {file.bytes !== undefined && (
                        <FileStat label="Size" value={file.bytes === null ? '-' : `${bytesToMB(file.bytes).toFixed(2)} MB`} />
                    )}

                    {/* TYPE */}
                    {file.bytes !== undefined && <FileStat label="Document Type" value={file.mimeType || '-'} />}

                    {/* STATUS */}
                    {file.status === FileStatusEnum.CHECKED_IN && (
                        <FileStat label="Status" value={<Badge color="blue" label="Checked-in" size="small" shape="squared" />} />
                    )}
                    {file.status === FileStatusEnum.CHECKED_OUT && (
                        <FileStat label="Status" value={<Badge color="red" label="Checked-out" size="small" shape="squared" />} />
                    )}
                </section>

                {file.recordType === FileRecordTypeEnum.REFERENCE && file.source?.path && (
                    <section className="mt-8">
                        <p className="font-medium pb-2 border-b text-gray-800">Original</p>
                        <FilePathSection filePath={file.source?.path} onGoToFolder={onGoToFolder} />
                    </section>
                )}

                {!isFolderType(file.recordType) && (
                    <FileReferences
                        fileId={file.id}
                        sourceFileId={file.source?.id || file.id}
                        onGoToFolder={onGoToFolder}
                        inContext={inContext}
                    />
                )}

                <section className="mt-8">
                    <p className="font-medium text-gray-800">History</p>
                </section>
            </div>
        </div>
    );
}

function FileStat({ label, value }: { label: string; value: ReactNode }): JSX.Element {
    return (
        <section className="py-4 border-b text-sm flex items-center justify-between">
            <p className="text-gray-500">{label}</p>
            <p className="text-gray-800">{value}</p>
        </section>
    );
}

function CreatedByStat({ user }: { user?: User | UserOverview }): JSX.Element {
    if (user) {
        const { firstName, lastName } = user;
        return <FileStat label="Created By" value={`${firstName} ${lastName}`} />;
    }
    return <FileStat label="Created By" value="-" />;
}

export default FileRecordDetails;
